import { AfterViewInit, ChangeDetectionStrategy, Component, computed, ElementRef, inject, model, signal, viewChild, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';

import type { ICellEditorAngularComp } from 'ag-grid-angular';
import { InputComponent } from '@softline/ui-core';
import { FormField } from '@angular/forms/signals';
import {StringColumn} from '../../types/grid'
import { filterEmpty, toArray } from '@softline/core';
import type { ICellEditorParams } from 'ag-grid-enterprise';

@Component({
  standalone: true,
  selector: 'soft-text-cell-editor',
  imports: [FormsModule, InputComponent, FormField],
  styles: `
    :host {
      display: flex;
      width: 100%;
      justify-content: center;
      align-items: center;
    }
  `,
  template: `<soft-input class="my-0.5" [(value)]="value" [errors]="errors()" #inputComp></soft-input>`,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TextCellEditor implements ICellEditorAngularComp, AfterViewInit, OnDestroy {
  private params!: ICellEditorParams;
  private readonly el = inject(ElementRef).nativeElement as HTMLElement;
  private resizeObserver?: ResizeObserver;

  protected input = viewChild<InputComponent>('inputComp');
  readonly col = signal<StringColumn<object> | null>(null);
  value = model('');

  readonly errors = computed(() => {
    const col = this.col();
    const params = this.params;
    const value = this.value();
    if (!col || !col.validate)
      return [];
    return filterEmpty(toArray(col.validate(value, params.data)));
  })

  agInit(params: ICellEditorParams): void {
    this.params = params;
    this.col.set(params['col']);
    this.value.set(this.getInitialValue(params));

    this.resizeObserver = new ResizeObserver(entries => {
      const width = entries[0].contentRect.width;
      const popupWrapper = this.el.closest('.ag-popup-editor') as HTMLElement | null;
      if (popupWrapper) {
        popupWrapper.style.width = `${width+24}px`;
      }
    });
    this.resizeObserver.observe(params.eGridCell);
  }

  refresh(params: ICellEditorParams<any, any>) {
    this.params = params;
    this.col.set(params['col']);
    this.value.set(params.value);
  }

  ngOnDestroy(): void {
    this.el.removeEventListener('keydown', this.onKeyDown, true);
    this.resizeObserver?.disconnect();
  }

  isCancelAfterEnd(): boolean {
    return this.errors().length > 0;
  }

  getValue(): any {
    return this.value();
  }

  getInitialValue(params: ICellEditorParams): any {
    let startValue = params.value;

    const eventKey = params.eventKey;
    const isBackspace = eventKey === 'Backspace';

    if (isBackspace) {
      startValue = '';
    } else if (eventKey && eventKey.length === 1) {
      startValue = eventKey;
    }

    if (startValue !== null && startValue !== undefined) {
      return startValue;
    }

    return '';
  }

  afterGuiAttached(): void {
    this.el.addEventListener('keydown', this.onKeyDown, true);
  }

  private onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Tab') {
      event.preventDefault();
      event.stopPropagation();

      if (this.errors().length > 0) return;

      const api = this.params.api;
      const colKey = this.params.column;
      const rowIndex = this.params.node.rowIndex ?? 0;
      const allCols = api.getAllDisplayedColumns();
      const currentIndex = allCols.findIndex(c => c === colKey);
      const direction = event.shiftKey ? -1 : 1;
      let nextColIndex = currentIndex + direction;
      let nextRowIndex = rowIndex;

      if (nextColIndex >= allCols.length) {
        nextColIndex = 0;
        nextRowIndex++;
      } else if (nextColIndex < 0) {
        nextColIndex = allCols.length - 1;
        nextRowIndex--;
      }

      this.params.stopEditing();

      if (nextRowIndex >= 0 && nextRowIndex < api.getDisplayedRowCount()) {
        const nextCol = allCols[nextColIndex];
        api.setFocusedCell(nextRowIndex, nextCol);
        api.startEditingCell({
          rowIndex: nextRowIndex,
          colKey: nextCol,
        });
      }
    }
  };

  ngAfterViewInit() {
    setTimeout(() => {
      const input = this.input();
      if(input)
        input?.focus()
    });
  }

  getValidationErrors(): string[] | null {
    return filterEmpty(this.errors().map(o => o.message));
  }
}
